/* eslint-disable import/no-unresolved */
import BaseElement from 'base-element'
import { css, html, ref, createRef } from 'lit-all'
import styles from 'lib-styles'
import { handleize } from 'lib-utils'
/* eslint-disable import/no-unresolved */

class HHCollectionFilters extends BaseElement {
  static properties = {
    products: { type: Array },
    filters: { type: Array },
    facetTagConfigurations: {
      type: Array,
      // attribute: 'facet-tag-configurations',
    },
    minPrice: { type: Number, attribute: false },
    maxPrice: { type: Number, attribute: false },
  }

  $form = createRef()

  constructor() {
    super()
    this.minPrice = 0
    this.maxPrice = 100
    this.facetTagConfigurations = []
  }

  updated(changedProperties) {
    if (changedProperties.has('products')) {
      this.minPrice = Math.floor(
        this.products.reduce(
          (acc, product) => Math.min(acc, product.minPrice),
          0
        )
      )
      this.maxPrice = Math.ceil(
        this.products.reduce(
          (acc, product) => Math.max(acc, product.maxPrice),
          0
        )
      )
    }
  }

  _handleFacetChange = (event) => {
    event.preventDefault()
    // Enforce only one checkbox for group allowed at a time (hybrid radio/checkbox behaviour)
    if (event.target.type === 'checkbox') {
      const checkboxes = this.renderRoot.querySelectorAll(
        `input[type="checkbox"][name="${event.target.name}"]`
      )
      checkboxes.forEach((checkbox) => {
        if (checkbox !== event.target) {
          checkbox.checked = false
        }
      })
    }
    const formData = new FormData(this.$form.value)
    const query = new URLSearchParams(formData)
    const url = `${window.location.pathname}?${query.toString()}`
    window.history.replaceState(null, null, url)
    this.dispatchEvent(new Event('change'))
    if (event.target.name === 'color') {
      // Optionally trigger automatic swatch change for all cards
      // document.dispatchEvent(
      //   new CustomEvent('hh:filter-swatch-changed', {
      //     bubbles: true,
      //     composed: true,
      //     detail: event.target.value,
      //   })
      // )
    }
  }

  _handleFormData(event) {
    const formData = event.formData
    // Augment form data with nested component input data
    const nestedPriceRange = this.$form.value.querySelector('hh-price-range')
    if (nestedPriceRange) {
      const priceRangeValues = nestedPriceRange.getValues()
      for (const key of Object.keys(priceRangeValues)) {
        const value = parseFloat(priceRangeValues[key])
        // Don't include min/max price if they are the same as the default
        if (key === 'price-min' && value === this.minPrice) continue
        if (key === 'price-max' && value === this.maxPrice) continue
        formData.append(key, value)
      }
    }
  }

  _renderTags(facet, enabledValues = []) {
    let facets = Array.from(
      this.products.reduce((acc, product) => {
        const facetValues = product['tags'].filter((_) =>
          _.includes(`${facet}:`)
        )
        facetValues.forEach((_) => acc.add(_.split(':')[1].trim()))
        return acc
      }, new Set())
    )
    if (!facets) return ''
    return html`
      <ul class="flex wrap gap05">
        ${facets
          .filter((_) => enabledValues.includes(_))
          .map((_, index) => {
            return html`
              <li class="unstyled">
                <input
                  type="checkbox"
                  @change=${this._handleFacetChange}
                  id=${`toggleTag${index}`}
                  class="toggle-tag"
                  .checked=${this.filters.find(
                    (__) => __.facet === facet && __.value.includes(_)
                  )}
                  name="tags"
                  value=${`${facet}: ${_}`}
                  hidden
                />
                <label for=${`toggleTag${index}`} class="tag"> ${_} </label>
              </li>
            `
          })}
      </ul>
    `
  }

  _renderSwatches() {
    const temp = []
    const swatches = this.products.reduce((acc, product) => {
      const newValues = []
      const swatchOption = product.options?.find((_) => _.name === 'Color')
      if (!swatchOption) return acc
      for (const value of swatchOption?.values) {
        if (temp.includes(value)) continue
        temp.push(value)
        newValues.push({
          color: value,
          handle: handleize(value),
        })
      }
      return [...acc, ...newValues]
    }, [])
    return html`
      <ul class="unstyled unordered-list flex gap05">
        ${swatches.map((_) => {
          const active = this.filters.find(
            (__) => __.facet === 'color' && __.value.includes(_.color)
          )
          const background = __THEME_GLOBALS__?.swatches[_.handle] || Array(2)
          return html`
            <li class="unstyled swatch-container" ?active=${active}>
              <input
                type="checkbox"
                name="color"
                id=${`swatch-${handleize(_.color)}`}
                value=${_.color}
                .checked=${active}
                hidden
                @change=${this._handleFacetChange}
                @click=${this._handleFacetClick}
              />
              <label for=${`swatch-${handleize(_.color)}`}>
                <div class="swatch-outline">
                  <span
                    class=${`swatch swatch--${_.color}`}
                    style=${`background: ${background[0]}; --secondary-color: ${background[1]}`}
                  ></span>
                </div>
                <span class="color-title">${_.color}</span>
              </label>
            </li>
          `
        })}
      </ul>
    `
  }

  _renderPriceRange() {
    const priceMinFilterValue = this.filters.find(
      (_) => _.facet === 'price-min'
    )?.value
    const priceMaxFilterValue = this.filters.find(
      (_) => _.facet === 'price-max'
    )?.value
    let low = this.minPrice
    let high = this.maxPrice
    // Constrain filters to allowed min/max
    if (priceMinFilterValue) {
      low = Math.max(this.minPrice, priceMinFilterValue)
    }
    if (priceMaxFilterValue) {
      high = Math.min(this.maxPrice, priceMaxFilterValue)
    }
    return html`
      <hh-price-range
        min=${this.minPrice}
        max=${this.maxPrice}
        low=${low}
        high=${high}
      >
      </hh-price-range>
    `
  }

  render() {
    return html`
      <form @formdata=${this._handleFormData} ${ref(this.$form)} class="w100">
        <div id="accordion-list">
          ${this.facetTagConfigurations.map(
            (config) => html`
              <div class="facet-group">
                <span class="facet-group__title">${config.label}</span>
                <div class="facet-group__content">
                  ${this._renderTags(config.facet, config.enabledValues)}
                </div>
              </div>
            `
          )}
          <!-- Uncomment to re-enable color filters
          <div class="facet-group facet-group--color">
            <span class="facet-group__title">Color</span>
            <div class="facet-group__content">${this._renderSwatches()}</div>
          </div> -->
        </div>
      </form>
    `
  }

  static styles = [
    styles.reset,
    styles.flex,
    styles.sizing,
    css`
      :host {
        display: flex;
        width: 100%;
      }
      .unordered-list {
        overflow-x: scroll;
        scrollbar-width: none;
      }
      form {
        border-color: #c2c2c2;
        border-style: solid;
        border-bottom-width: 1px;
        border-top-width: 1px;
        padding-bottom: 26px;
      }
      #accordion-list {
        display: flex;
        flex-direction: column;
      }
      .facet-group {
        flex: 1;
      }
      .facet-group__title {
        display: block;
        font-family: Inter, sans-serif;
        font-weight: 500;
        font-size: 16px;
        line-height: 20px;
        margin: 20px 0 10px;
      }

      .swatch-container .color-title {
        text-wrap: nowrap;
        white-space: nowrap;
        word-wrap: break-word;
        overflow: auto;
      }

      .swatch-container label {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .swatch-container {
        display: flex;
        gap: 10px;
        align-items: center;
        padding: 8px;
        border-radius: 12px;
        position: relative;
      }

      .swatch-container[active] {
        background-color: #000;
      }
      .swatch-container[active] .color-title {
        color: #fff;
      }

      .swatch-outline {
        border: 2px solid transparent;
        border-radius: 50%;
      }

      .swatch-container[active] .swatch-outline {
        border: 2px solid #000;
      }

      .swatch {
        background: transparent;
        border-radius: 50%;
        border: 2px solid #ababab;
        cursor: pointer;
        display: block;
        width: var(--swatch-size, 24px);
        height: var(--swatch-size, 24px);
        position: relative;
        transition: opacity 0.2s;
        overflow: hidden;
      }
      .swatch:after {
        content: '';
        background-color: var(--secondary-color);
        display: block;
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        left: 50%;
      }
      .tag {
        display: inline-block;
        padding: 14px 20px;
        border: 1px solid #c2c2c2;
        border-radius: 12px;
        background-color: transparent;
        color: #000;
        cursor: pointer;
        user-select: none; /* Prevent text selection */
        font-weight: 500;
        font-size: 12px;
        line-height: 130%;
        white-space: nowrap;
      }
      .toggle-tag:checked + .tag {
        background-color: #ebe9e9;
        border-color: #000;
      }

      @media screen and (min-width: 992px) {
        .swatch-container[active] div {
          border: 1px solid #000;
        }
        .swatch-container label {
          font-weight: 500;
          font-size: 12px;
          line-height: 16px;
        }
        .swatch-container .color-title {
          display: none;
          position: absolute;
          transform: translateX(calc(-50% + 14px));
          top: 40px;
          white-space: normal;
          max-width: 50px;
          text-align: center;
        }
        .swatch-container[active] .color-title,
        .swatch-container:hover .color-title {
          display: block;
        }
        .swatch-container[active] {
          background-color: unset;
        }
        .swatch-container[active] .color-title {
          color: unset;
        }
        .facet-group--color {
          width: 50%;
        }
        .facet-group--color .facet-group__content ul {
          padding-bottom: 2em;
          gap: 20px;
          flex-wrap: wrap;
        }
        .facet-group__title {
          margin: 32px 0 16px;
        }
        .tag {
          border-radius: 32px;
        }
        #accordion-list {
          display: flex;
          flex-direction: row;
        }
        form {
          border: none;
        }
      }
    `,
  ]
}

customElements.define('hh-collection-filters', HHCollectionFilters)
